/**
 * DurationPicker — iOS-alarm-style wheel picker for selecting duration.
 *
 * Uses CSS scroll-snap + native inertial scrolling for smooth, touch-friendly UX.
 * Two wheels: numeric value (left) and unit (right).
 * When "permanent" is selected, the value wheel collapses and shows "Permanent Access".
 *
 * Props:
 *   value: { type: 'minutes'|'hours'|'days'|'weeks'|'permanent', value: number }
 *   onChange(next): called with the new {type, value} whenever either wheel stops
 *   allowMinutes: bool (default true) — show Minutes in unit list
 *   testIdPrefix: string — appended to data-testid attributes
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const ITEM_HEIGHT = 40;           // px per row
const VISIBLE_ROWS = 5;           // 2 above + center + 2 below
const WHEEL_HEIGHT = ITEM_HEIGHT * VISIBLE_ROWS;
const PAD_ROWS = Math.floor(VISIBLE_ROWS / 2);  // 2

const DEFAULT_VALUES = [1, 2, 3, 4, 5, 10, 12, 15, 24, 30, 48, 60, 90, 100];

const Wheel = ({ items, renderItem, selectedIndex, onSelect, ariaLabel, testId }) => {
    const scrollRef = useRef(null);
    const settleTimerRef = useRef(null);
    const programmaticScrollRef = useRef(false);

    // Scroll to a given index on mount or when selectedIndex changes externally
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const target = selectedIndex * ITEM_HEIGHT;
        if (Math.abs(el.scrollTop - target) < 1) return;
        programmaticScrollRef.current = true;
        el.scrollTop = target;
        // release flag after layout
        requestAnimationFrame(() => { programmaticScrollRef.current = false; });
    }, [selectedIndex]);

    const handleScroll = useCallback(() => {
        if (programmaticScrollRef.current) return;
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = setTimeout(() => {
            const el = scrollRef.current;
            if (!el) return;
            const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
            const clamped = Math.max(0, Math.min(items.length - 1, idx));
            // Snap scrollTop to the exact row
            const snapTo = clamped * ITEM_HEIGHT;
            if (Math.abs(el.scrollTop - snapTo) > 0.5) {
                programmaticScrollRef.current = true;
                el.scrollTo({ top: snapTo, behavior: 'smooth' });
                setTimeout(() => { programmaticScrollRef.current = false; }, 200);
            }
            if (clamped !== selectedIndex) {
                onSelect(clamped);
                try { if (navigator.vibrate) navigator.vibrate(5); } catch { /* noop */ }
            }
        }, 100);
    }, [items.length, onSelect, selectedIndex]);

    return (
        <div className="relative" style={{ height: WHEEL_HEIGHT }} data-testid={testId}>
            {/* Center highlight band */}
            <div
                aria-hidden
                className="absolute left-0 right-0 pointer-events-none border-y border-fuchsia-500/40 bg-fuchsia-500/5"
                style={{ top: PAD_ROWS * ITEM_HEIGHT, height: ITEM_HEIGHT }}
            />
            {/* Top gradient fade */}
            <div aria-hidden className="absolute top-0 left-0 right-0 pointer-events-none bg-gradient-to-b from-background to-transparent z-10"
                 style={{ height: PAD_ROWS * ITEM_HEIGHT }} />
            <div aria-hidden className="absolute bottom-0 left-0 right-0 pointer-events-none bg-gradient-to-t from-background to-transparent z-10"
                 style={{ height: PAD_ROWS * ITEM_HEIGHT }} />

            <div
                ref={scrollRef}
                onScroll={handleScroll}
                aria-label={ariaLabel}
                role="listbox"
                tabIndex={0}
                className="w-full h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
                style={{
                    scrollSnapType: 'y mandatory',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                }}
            >
                {/* top padding rows for first item to center */}
                <div style={{ height: PAD_ROWS * ITEM_HEIGHT }} />
                {items.map((item, i) => {
                    const isSelected = i === selectedIndex;
                    return (
                        <div
                            key={`${i}-${item}`}
                            role="option"
                            aria-selected={isSelected}
                            data-testid={`${testId}-item-${item}`}
                            className={`flex items-center justify-center snap-center select-none transition-all duration-150 ${
                                isSelected ? 'text-foreground font-semibold text-xl' : 'text-muted-foreground text-base'
                            }`}
                            style={{ height: ITEM_HEIGHT }}
                            onClick={() => {
                                const el = scrollRef.current;
                                if (el) el.scrollTo({ top: i * ITEM_HEIGHT, behavior: 'smooth' });
                            }}
                        >
                            {renderItem ? renderItem(item) : item}
                        </div>
                    );
                })}
                <div style={{ height: PAD_ROWS * ITEM_HEIGHT }} />
            </div>
        </div>
    );
};

export const DurationPicker = ({
    value = { type: 'hours', value: 24 },
    onChange,
    allowMinutes = true,
    testIdPrefix = 'duration',
}) => {
    const { t } = useTranslation();

    const UNITS = [
        ...(allowMinutes ? [{ key: 'minutes', label: t('manager.durationMinutes') }] : []),
        { key: 'hours', label: t('manager.durationHours') },
        { key: 'days', label: t('manager.durationDays') },
        { key: 'weeks', label: t('manager.durationWeeks') },
        { key: 'permanent', label: t('manager.durationPermanent') },
    ];

    const VALUES = DEFAULT_VALUES;

    const [valueIdx, setValueIdx] = useState(() => {
        const idx = VALUES.indexOf(value?.value ?? 24);
        return idx >= 0 ? idx : VALUES.indexOf(24);
    });
    const [unitIdx, setUnitIdx] = useState(() => {
        const idx = UNITS.findIndex(u => u.key === (value?.type ?? 'hours'));
        return idx >= 0 ? idx : UNITS.findIndex(u => u.key === 'hours');
    });

    const isPermanent = UNITS[unitIdx]?.key === 'permanent';

    // Emit change whenever state changes
    useEffect(() => {
        const unit = UNITS[unitIdx];
        if (!unit) return;
        if (unit.key === 'permanent') {
            onChange?.({ type: 'permanent', value: null });
        } else {
            onChange?.({ type: unit.key, value: VALUES[valueIdx] });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueIdx, unitIdx]);

    return (
        <div className="w-full" data-testid={`${testIdPrefix}-picker`}>
            {isPermanent ? (
                <div
                    className="flex flex-col items-center justify-center gap-2 bg-slate-900/40 border border-fuchsia-500/30 rounded-xl py-6"
                    style={{ height: WHEEL_HEIGHT }}
                    data-testid={`${testIdPrefix}-permanent-state`}
                >
                    <span className="text-3xl">♾️</span>
                    <p className="text-lg font-semibold text-foreground">{t('manager.permanentAccess')}</p>
                </div>
            ) : null}
            <div className={`grid grid-cols-2 gap-2 bg-slate-900/40 rounded-xl px-1 ${isPermanent ? 'hidden' : ''}`}>
                <Wheel
                    items={VALUES}
                    selectedIndex={valueIdx}
                    onSelect={setValueIdx}
                    ariaLabel={t('manager.durationValuePicker')}
                    testId={`${testIdPrefix}-value-wheel`}
                />
                <Wheel
                    items={UNITS.filter(u => u.key !== 'permanent').map(u => u.label)}
                    selectedIndex={Math.min(unitIdx, UNITS.length - 2)}
                    onSelect={(i) => setUnitIdx(i)}
                    ariaLabel={t('manager.durationUnitPicker')}
                    testId={`${testIdPrefix}-unit-wheel`}
                />
            </div>
            {/* Permanent toggle row — always available */}
            <div className="flex items-center justify-center mt-2">
                <button
                    type="button"
                    onClick={() => {
                        const permIdx = UNITS.findIndex(u => u.key === 'permanent');
                        setUnitIdx(isPermanent ? UNITS.findIndex(u => u.key === 'hours') : permIdx);
                    }}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                        isPermanent
                            ? 'bg-fuchsia-600 text-white hover:bg-fuchsia-700'
                            : 'border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                    data-testid={`${testIdPrefix}-permanent-toggle`}
                >
                    {isPermanent ? `✓ ${t('manager.durationPermanent')}` : t('manager.durationPermanent')}
                </button>
            </div>
        </div>
    );
};

export default DurationPicker;
